import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FocusLock from 'react-focus-lock';
import Button from '~/Components/Global/Form/Button';
import { closeModal, setModalData, openModal } from '~/Redux/Modules/Modal';
import { ADD_ALARM_MODAL, TASK_SETTINGS_MODAL } from '~/Config/Modals';
import moment from '~/Providers/Moment';
import Checkbox from '~/Components/Global/Form/Checkbox';
import DatePicker from '~/Components/Global/DatePicker/DatePicker';
import CustomTimePicker from '~/Components/Global/CustomTimePicker/CustomTimePicker';
import { DATE_FORMAT } from '~/Redux/Constants';

import './AddAlarmModal.scss';

const TIME_MAP = {
  morning: '08:00',
  afternoon: '13:00',
  night: '20:00',
};

const AddAlarmModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(state => state.Modal.modalsOpen.includes(ADD_ALARM_MODAL));
  const modalInfo = useSelector(state => state.Modal.modalData);
  const userOptions = useSelector(state => state.UserOptions.data);
  const use_intl_time = userOptions?.use_intl_time || false;

  const task = isOpen && modalInfo?.modalType === ADD_ALARM_MODAL ? modalInfo.task : null;
  const onSaveCallback =
    isOpen && modalInfo?.modalType === ADD_ALARM_MODAL ? modalInfo.onSave : null;
  const onCancelCallback =
    isOpen && modalInfo?.modalType === ADD_ALARM_MODAL ? modalInfo.onCancel : null;
  const positionRect =
    isOpen && modalInfo?.modalType === ADD_ALARM_MODAL ? modalInfo.positionRect : null;

  const [taskAlarms, setTaskAlarms] = useState([]);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedTimeOption, setSelectedTimeOption] = useState(null);
  const [alarmDateTime, setAlarmDateTime] = useState(null);
  const [showCustomTimePicker, setShowCustomTimePicker] = useState(false);
  const [customTimeDisplay, setCustomTimeDisplay] = useState('');
  const [customTime, setCustomTime] = useState(
    moment()
      .hour(12)
      .minute(30)
  );

  const customTimePickerRef = useRef();

  const timeOptions = [
    {
      id: 'morning',
      label: 'Morning',
      time: use_intl_time ? '8:00' : '8:00 am',
    },
    {
      id: 'afternoon',
      label: 'Afternoon',
      time: use_intl_time ? '13:00' : '1:00 pm',
    },
    {
      id: 'night',
      label: 'Night',
      time: use_intl_time ? '20:00' : '8:00 pm',
    },
    { id: 'custom', label: 'Custom' },
  ];

  const handleBack = () => {
    dispatch(closeModal(ADD_ALARM_MODAL));
    dispatch(setModalData(null));

    if (task) {
      dispatch(
        setModalData({
          task: { ...task, alarms: task.alarms || [] },
          onSave: onSaveCallback,
          onCancel: onCancelCallback,
          positionRect,
          modalType: TASK_SETTINGS_MODAL,
        })
      );
      dispatch(openModal(TASK_SETTINGS_MODAL));
    }
  };

  const handleUpdateAlarm = () => {
    handleAlarmUpdate();
  };

  const handleAlarmUpdate = () => {
    const newAlarm = buildAlarmObject(selectedDate, selectedTimeOption);
    const currentTaskAlarms = task?.alarms || [];
    const updatedAlarms = [...currentTaskAlarms, newAlarm];

    dispatch(closeModal(ADD_ALARM_MODAL));
    dispatch(
      setModalData({
        task: task,
        onSave: onSaveCallback,
        onCancel: onCancelCallback,
        positionRect,
        modalType: TASK_SETTINGS_MODAL,
        pendingAlarms: updatedAlarms,
      })
    );
    dispatch(openModal(TASK_SETTINGS_MODAL));
  };

  const buildAlarmObject = (date, timeOption) => {
    let timeString;

    if (timeOption === 'custom') {
      timeString = customTime.format('HH:mm');
    } else {
      timeString = TIME_MAP[timeOption];
    }
    const dateString = date.format('YYYY-MM-DD');
    const targetDatetime = `${dateString} ${timeString}:00`;

    return {
      target_datetime: targetDatetime,
      channel: 'push',
    };
  };

  const handleTimeOptionChange = optionId => {
    setSelectedTimeOption(optionId);

    if (optionId !== 'custom') {
      const timeString = TIME_MAP[optionId];
      const dateString = selectedDate.format('YYYY-MM-DD');
      const isoDateTime = moment(`${dateString} ${timeString}`, 'YYYY-MM-DD HH:mm').toISOString();
      setAlarmDateTime(isoDateTime);
    }
  };

  const handleCustomTimeBack = () => {
    setShowCustomTimePicker(false);
  };

  const handleCustomTimeSave = (customTime, displayTime) => {
    const timeString = customTime.format('HH:mm');
    const dateString = selectedDate.format('YYYY-MM-DD');
    const isoDateTime = moment(`${dateString} ${timeString}`, 'YYYY-MM-DD HH:mm').toISOString();

    setAlarmDateTime(isoDateTime);
    setCustomTimeDisplay(displayTime);
    setCustomTime(customTime);
    setSelectedTimeOption('custom');
    setShowCustomTimePicker(false);
  };

  const handleShowCustomTimePicker = () => {
    setSelectedTimeOption('custom');
    setShowCustomTimePicker(true);
  };

  const customProps = {
    role: 'button',
    tabIndex: '0',
    onClick: handleShowCustomTimePicker,
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') e.target.click();
    },
    'aria-label': 'Set custom time',
  };

  const handleFooterButtonClick = () => {
    if (showCustomTimePicker) {
      customTimePickerRef.current?.handleSave();
    } else {
      handleUpdateAlarm();
    }
  };

  const handleDateChange = date => {
    setSelectedDate(date);
  };

  const isValidAlarmData = () => {
    if (!selectedDate || !selectedTimeOption) {
      return false;
    }

    const today = moment().startOf('day');
    if (selectedDate.isBefore(today, 'day')) {
      return false;
    }

    if (selectedTimeOption === 'custom') {
      const isValid = customTime && customTime.isValid() && alarmDateTime;
      return isValid;
    }

    return alarmDateTime;
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('alarm-modal-open');
      setTaskAlarms(task?.alarms || []);

      if (task && task.start) {
        setSelectedDate(moment(task.start, DATE_FORMAT));
      } else {
        setSelectedDate(moment());
      }

      setSelectedTimeOption(null);
      setCustomTime(
        moment()
          .hour(12)
          .minute(30)
      );
      setCustomTimeDisplay('');
      setShowCustomTimePicker(false);
      setAlarmDateTime(null);
    }

    return () => {
      document.body.classList.remove('alarm-modal-open');
    };
  }, [isOpen, task]);

  if (!isOpen || !modalInfo || modalInfo.modalType !== ADD_ALARM_MODAL) return null;

  let style = {};
  if (positionRect) {
    style = {
      position: 'absolute',
      // AddAlarmModal is ~350px tall (header + date section + time options + footer)
      top:
        positionRect.top > window.innerHeight - 350 ? window.innerHeight - 350 : positionRect.top,
      // AddAlarmModal is 280px wide, so half is 140px
      left:
        positionRect.left > window.innerWidth - 140 ? window.innerWidth - 140 : positionRect.left,
      transform: 'translateX(-50%)',
    };
  }

  const handleWrapperClick = () => {
    handleBack();
  };

  return (
    <div className="alarm-modal-wrapper" onClick={handleWrapperClick}>
      <div
        className="alarm-modal"
        style={style}
        onClick={e => e.stopPropagation()}
        data-stop-drop-down-close
        role="dialog"
        aria-modal="true"
        aria-labelledby="alarm-modal-title"
      >
        <FocusLock>
          <div className="alarm-modal__header" id="alarm-modal-title">
            <button
              className="back-button"
              onClick={showCustomTimePicker ? handleCustomTimeBack : handleBack}
              aria-label={
                showCustomTimePicker ? 'Go back to alarm options' : 'Go back to task settings'
              }
            >
              <span className="material-icons-outlined back-button-icon">chevron_left</span>
            </button>
            <span>{showCustomTimePicker ? 'Custom' : 'New Alarm'}</span>
          </div>

          {showCustomTimePicker ? (
            <div className="alarm-modal__body">
              <div className="alarm-modal__date-section">
                <DatePicker
                  className="minimal-input inline-input"
                  name="alarmDate"
                  label="On"
                  value={selectedDate}
                  onChange={handleDateChange}
                  showTimeSelect={false}
                  showDayOfWeek
                  preventOpenOnFocus
                  shouldCloseOnSelect
                />
              </div>

              <CustomTimePicker
                ref={customTimePickerRef}
                onSave={handleCustomTimeSave}
                onBack={handleCustomTimeBack}
                initialTime={customTime}
                showHeaderAndFooter={false}
              />
            </div>
          ) : (
            <div className="alarm-modal__body">
              <div className="alarm-modal__date-section">
                <DatePicker
                  className="minimal-input inline-input"
                  name="alarmDate"
                  label="On"
                  value={selectedDate}
                  onChange={handleDateChange}
                  showTimeSelect={false}
                  showDayOfWeek
                  preventOpenOnFocus
                  shouldCloseOnSelect
                />
              </div>

              <div className="alarm-modal__time-options">
                {timeOptions.map(option => (
                  <div
                    key={option.id}
                    className={`time-option ${option.id === 'custom' ? 'custom-option' : ''}`}
                    {...(option.id === 'custom' ? customProps : {})}
                  >
                    <Checkbox
                      name={option.id}
                      label={option.label}
                      value={selectedTimeOption === option.id}
                      onChange={() => handleTimeOptionChange(option.id)}
                      formatted={false}
                      className="alarm-checkbox"
                    />
                    {option.time ? (
                      <span className="time-value">{option.time}</span>
                    ) : (
                      <span className="material-icons-outlined custom-chevron">chevron_right</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="alarm-modal__footer">
            <Button
              text={showCustomTimePicker ? 'SAVE CUSTOM TIME' : 'UPDATE ALARMS'}
              className="btn btn-default update-alarm-button"
              onClick={handleFooterButtonClick}
              disabled={!showCustomTimePicker && !isValidAlarmData()}
            />
          </div>
        </FocusLock>
      </div>
    </div>
  );
};

export default AddAlarmModal;
